/**
 * Development list component
 */

/** Dependencies -------------------------------------------- */

// External

import { forwardRef, useEffect, useRef, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { VariableSizeList } from 'react-window'
import { throttle } from 'throttle-debounce'

// Internal

import { getPlugin, normalizeDevelopment } from '../dictionary'
import { useAppState } from '../state'

import { noop } from '../../helpers'

import Development from './development'

/** Constants ----------------------------------------------- */

const defaultListItemHeight = 180

/** Main ---------------------------------------------------- */

const baseClassName = 'development-list'

function DevelopmentListItem( props ) {

    const {
        index,
        style,
        setHeight = noop,
        children,
        ...additionalProps
    } = props

    const itemInnerRef = useRef()

    // Effects

    function itemInnerRefEffect() {

        if ( itemInnerRef.current ) {
            setHeight( index, itemInnerRef.current.clientHeight )
        }
    }

    useEffect( itemInnerRefEffect, [ itemInnerRef.current ] )

    return (
        <li className={ `${baseClassName}__item` } style={ style } { ...additionalProps } >
            <div className={ `${baseClassName}__item-inner` } ref={ itemInnerRef }>
                <div className={ `${baseClassName}__item-inner-inner` }>
                    { children }
                </div>
            </div>
        </li>
    )
}

function DevelopmentsListInner( props, ref ) {

    return (
        <ul className={ `${baseClassName}__list` } { ...props } ref={ ref }/>
    )
}

export default function DevelopmentList() {

    // Constants

    const plugin = getPlugin()

    const aggregate = useAppState( ( state ) => state.aggregate )

    const developments = useAppState( ( state ) => state.developments )
    const developmentsProps = developments.map( normalizeDevelopment )
    const development = useAppState( ( state ) => state.development )

    const listRef = useRef()
    const listItemHeights = useRef()

    const [ defaultScrollPosition, setDefaultScrollPosition ] = useState( 0 )
    const [ aggregateScrollPosition, setAggregateScrollPosition ] = useState( 0 )

    // Helpers

    function getListItemHeight( index ) {

        return listItemHeights.current
            ? listItemHeights.current[index] || defaultListItemHeight
            : defaultListItemHeight
    }

    function setListItemHeight( index, height ) {

        if ( listRef.current ) {
            listRef.current.resetAfterIndex( 0 )
        }

        listItemHeights.current = { ...listItemHeights.current, [index]: height }
    }

    // Effects

    function developmentsEffect() {
        if ( listRef.current ) {
            listRef.current.scrollTo( 0 )
        }
    }

    useEffect( developmentsEffect, [ developments ] )

    function aggregateDevelopmentEffect() {

        if ( ! listRef.current || development ) return

        if ( aggregate ) {
            listRef.current.scrollTo( aggregateScrollPosition )
            return
        }

        setAggregateScrollPosition( 0 )
        listRef.current.scrollTo( defaultScrollPosition )
    }

    useEffect( aggregateDevelopmentEffect, [ aggregate, development ] )

    // Handlers

    function handleScroll( { scrollOffset } ) {

        if ( development ) return

        if ( aggregate ) {
            setAggregateScrollPosition( scrollOffset )
            return
        }
        
        setDefaultScrollPosition( scrollOffset )
    }

    // Render

    return (
        <div className={ baseClassName }>
            <AutoSizer className={ `${baseClassName}__auto-sizer` }>
                { ( { height, width } ) => (
                    <VariableSizeList
                        className={ `${baseClassName}__scroller` }
                        disableWidth={ true }
                        height={ height }
                        width={ width }
                        itemCount={ developmentsProps.length }
                        itemData={ developmentsProps }
                        itemSize={ getListItemHeight }
                        innerElementType={ forwardRef( DevelopmentsListInner ) }
                        onScroll={ throttle( plugin.scrollThrottle, handleScroll ) }
                        ref={ listRef }
                    >
                        { ( { index, style} ) => (
                            <DevelopmentListItem
                                index={ index }
                                style={ style }
                                setHeight={ setListItemHeight }
                            >
                                <Development
                                isFull={ !! development }
                                showAggregate={ ! aggregate }
                                { ...developmentsProps[index] }
                            />
                            </DevelopmentListItem>
                        ) }
                    </VariableSizeList>
                ) }
            </AutoSizer>
        </div>
    )
}
